import {Inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {LibConfig, LibConfigService} from '../lib.config';
import {Order} from '../_models/order';
import {Menu} from '../_models/menu';
import {NavController} from '@ionic/angular';
import {Constants} from '../_constants/constants';
import {ToastService} from './toast.service';
import {OrderItem} from '../_models/order-item';
import {User} from '../_models/user';
import {PickupLocation} from '../_models/pickup-location';
import {PickupLocationMenuService} from './pickup-location-menu.service';
import {BaseObservableService} from './base-obeservable-service';
import {Tab} from '../_models/tab';
import {LoadingService} from './loading.service';
import {TabService} from './tab.service';
import {getTimeBased} from '../_utils/local-storage-utils';
import {Availabilities} from '../_constants/availabilities';
import {MenuItemPrice} from '../_models/menu-item-price';
import {OrderStatuses} from '../_constants/order-statuses';
import {CustomerAreaPositionService} from './customer-area-position.service';
import {CustomerAreaPosition} from '../_models/customer-area-position';
import {PickupLocationMenu} from '../_models/pickup-location-menu';
import {DeviceService} from './device.service';
import {UserService} from './user.service';
import {OrganizationEvent} from '../_models/organization-event';
import {v4 as uuidv4} from 'uuid';
import {OrganizationTerminalService} from './organization-terminal.service';
import {TabStatuses} from '../_constants/tab-statuses';

@Injectable({
    providedIn: 'root'
})
export class OrderService extends BaseObservableService<Order> {
    destination = 'orders';
    orderModal: any;
    viewingOrder: Order;
    customerAreaPosition: CustomerAreaPosition;
    user: User;
    lockOrder = false;
    deviceId: string;
    cardFullText = true;

    public orderType: Observable<string>;
    private orderTypeSubject: BehaviorSubject<string>;

    public activeOrders: Observable<Order[]>;
    private activeOrdersSubject: BehaviorSubject<Order[]>;

    constructor(
        http: HttpClient,
        private organizationTerminalService: OrganizationTerminalService,
        private customerAreaPositionService: CustomerAreaPositionService,
        private userService: UserService,
        private toastService: ToastService,
        private deviceService: DeviceService,
        private pickupLocationMenuService: PickupLocationMenuService,
        private loadingService: LoadingService,
        private tabService: TabService,
        private navController: NavController,
        @Inject(LibConfigService) config: LibConfig
    ) {
        super(http, config);
        this.saveToLocalStorage = true;
        const val = getTimeBased(this.destination);
        if (!!val) {
            val.preload = true;
            this.setCurrent(new Order(val));
        }

        this.orderTypeSubject = new BehaviorSubject<string>(Availabilities.IN_HOUSE);
        this.orderType = this.orderTypeSubject.asObservable();

        this.activeOrdersSubject = new BehaviorSubject<Order[]>([]);
        this.activeOrders = this.activeOrdersSubject.asObservable();

        this.deviceService.getDeviceId().then(d => this.deviceId = d);

        this.customerAreaPositionService.current.subscribe(c => this.customerAreaPosition = !!c ? new CustomerAreaPosition(c) : null);
        this.userService.current.subscribe(u => this.user = !!u ? new User(u) : null);

        this.pickupLocationMenuService.current.subscribe(async pickupLocationMenu => {
            if (!!pickupLocationMenu) {
                const currentOrder = this.currentValue;
                const currentTab = this.tabService.currentValue;
                if (!this.lockOrder && !!currentOrder && currentOrder.status === OrderStatuses.PENDING
                    && pickupLocationMenu.parentId === currentOrder.pickupLocation.id
                    && !!currentOrder.items && currentOrder.items.length > 0) {
                    const sent = [];
                    currentOrder.items = currentOrder.items.filter(i => {
                        if (!i.selectedPrice) {
                            return true;
                        }

                        const menuItem = pickupLocationMenu.menu.menuItems.find(mi => !!i.menuItem && i.menuItem.related(mi));
                        i.selectedPrice.pickupLocationMenuItemPrice = !!pickupLocationMenu.menuItemPrices
                            ? pickupLocationMenu.menuItemPrices.find(p => p.menuItemPrice.id === i.selectedPrice.id) : null;

                        const price = new MenuItemPrice(i.selectedPrice);

                        const retVal = !menuItem
                            || (i.orderType === Availabilities.IN_HOUSE
                                && pickupLocationMenu.allowInHouse
                                && menuItem.allowInHouse()
                                && price.allowInHouse()
                                && (currentOrder.terminalOrder || menuItem.allowMobileOrdering()))
                            || (i.orderType === Availabilities.TOGO
                                && pickupLocationMenu.allowTogo
                                && menuItem.allowToGo()
                                && price.allowToGo()
                                && (currentOrder.terminalOrder || menuItem.allowMobileOrdering()));

                        if (!retVal && !sent.includes(i.menuItem.name)) {
                            this.toastService.message(`${i.menuItem.name} is no longer available and it has been removed from your current order.`);
                            sent.push(i.menuItem.name);
                        }
                        return retVal;
                    });

                    if (sent.length > 0 && currentOrder.items.length > 0) {
                        this.createPending(currentOrder).subscribe((t: Tab) => {
                            const i = !!t ? t.orders.find(u => u.id === currentOrder.id) : null;
                            this.setCurrent(!!i ? new Order(i) : null);
                        });
                    }

                    if (!!currentTab && !!currentTab.orders && currentTab.orders.length > 0) {
                        const index = currentTab.orders.findIndex(o => o.id === currentOrder.id);
                        currentTab.orders[index] = currentOrder;
                        this.tabService.setCurrent(currentTab);
                    }

                    if (currentOrder.items.length > 0) {
                        this.setCurrent(currentOrder);
                    } else {
                        this.setCurrent(null);
                    }
                }
            }
        });

        this.tabService.event.subscribe(t => {
            if (!!t && (t.status === TabStatuses.OPEN || t.status === TabStatuses.CLOSED) && !!t.orders) {
                const active = this.activeOrdersSubject.value;
                t.orders.forEach(o => {
                    const index = active.findIndex(a => a.id === o.id || (!!a.key && !!o.key && a.key === o.key));
                    if (index > -1) {
                        active.splice(index, 1);
                    }
                    if (OrderStatuses.isActive(o.status)) {
                        active.push(o);
                    }
                });
                this.setActiveOrders(active);
            }
        });
    }

    refresh() {
        if (!!this.currentValue && !!this.currentValue.id) {
            this.getSilent(this.currentValue.id).subscribe((o: Order) => this.setCurrent(new Order(o)));
        }
    }

    activateOrder(order: Order) {
        const headers = new HttpHeaders().set(Constants.IGNORE_ERROR_CODES, '422');
        return this.http.post(`${this.baseUrl()}/activate`, order, {headers});
    }

    createPending(order: Order) {
        const o = new Order(order);
        o.menu = null;
        o.customerAreaPosition = this.customerAreaPosition;
        o.items.forEach(i => this.setToppings(i));

        const headers = new HttpHeaders().set(Constants.IGNORE_ERROR_CODES, '417,423,424');
        return this.http.post(`${this.baseUrl()}/pending`, o, {headers});
    }

    setToppings(orderItem: OrderItem) {
        if (!orderItem.includedToppings) {
            orderItem.includedToppings = [];
        }
        if (!orderItem.optionalToppings) {
            orderItem.optionalToppings = [];
        }

        orderItem.toppings = [
            ...orderItem.includedToppings,
            ...orderItem.addedToppingsFilter()
        ];

        orderItem.selections.forEach(s => {
            s.selectedItems.forEach(si => this.setToppings(si.orderItem));
        });
    }

    initialize(menu: Menu, pickupLocation: PickupLocation) {
        const order = new Order({});
        order.menu = new Menu({id: menu.id});
        order.submittedBy = !!this.user ? this.user.person : null;
        order.pickupLocation = new PickupLocation({id: pickupLocation.id, name: pickupLocation.name});
        order.organizationEvent = !!pickupLocation.activeEvent ? new OrganizationEvent({
            id: pickupLocation.activeEvent.id,
            name: pickupLocation.activeEvent.name
        }) : null;
        order.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        order.key = uuidv4();
        order.status = OrderStatuses.PENDING;
        order.terminalOrder = this.organizationTerminalService.isTerminal;
        return order;
    }

    updateTableStandMarker(order: Order) {
        return this.http.put(`${this.baseUrl()}/${order.id}/table-stand-marker`, order);
    }

    updateStatus(id: number, status: string, terminalId: string, restock: boolean, marker?: number) {
        const headers = new HttpHeaders().set(Constants.IGNORE_ERROR_CODES, '410,422,423,428');
        let url = `${this.baseUrl()}/${id}/status/${status}?restock=${restock}`;
        if (!!terminalId) {
            url += `&terminalId=${terminalId}`;
        }
        if (!!marker) {
            url += `&marker=${marker}`;
        }
        return this.http.put(url, {}, {headers});
    }

    move(id: number, tabId: number, orderItemIds: number[] = null) {
        const headers = new HttpHeaders().set(Constants.IGNORE_ERROR_CODES, '409');
        return this.http.put(`${this.baseUrl()}/${id}/move${!!tabId ? ('/' + tabId) : ''}` + (!!orderItemIds && orderItemIds.length
            ? `?selectedOrderItemIds=${orderItemIds.join(',')}`
            : ''), {}, {headers});
    }

    separate(id: number, status: string, items: string, terminalId: string, restock: boolean, marker?: number) {
        const headers = new HttpHeaders().set(Constants.IGNORE_ERROR_CODES, '410,422,423,428');
        let url = `${this.baseUrl()}/${id}/separate/${status}/${items}?restock=${restock}`;
        if (!!terminalId) {
            url += `&terminalId=${terminalId}`;
        }
        if (!!marker) {
            url += `&marker=${marker}`;
        }
        return this.http.put(url, {}, {headers});
    }

    updateOrderType(id: number, type: number) {
        return this.http.put(`${this.baseUrl()}/${id}/order-type/${type}`, {});
    }

    loadCurrent() {
        return this.http.get(`${this.baseUrl()}/current`);
    }

    loadDeviceCurrent(deviceId: string) {
        return this.http.get(`${this.baseUrl()}/current/${deviceId}`);
    }

    missed(date: Date) {
        return this.http.get(`${this.baseUrl()}/missed?last=${date.toISOString()}`);
    }

    getTab(orderId) {
        return this.http.get(`${this.baseUrl()}/${orderId}/tab`);
    }

    public getCurrentActiveOrders(): Order[] {
        const active = this.activeOrdersSubject.value;
        return !!active ? active : [];
    }

    addActiveOrder(order: Order) {
        const active = this.getCurrentActiveOrders();
        const index = active.findIndex(o => o.id === order.id);
        if (index > -1) {
            active[index] = order;
        } else {
            active.push(order);
        }
        this.setActiveOrders(active);
    }

    getOrderType() {
        return this.orderTypeSubject.getValue();
    }

    nextType(type: string) {
        this.orderTypeSubject.next(type);
    }

    setActiveOrders(orders: Order[]) {
        this.activeOrdersSubject.next(orders);
    }

    async confirmOrder(order: Order) {
        this.loadingService.present();

        // need to init to populate the toppings
        order.items.forEach(i => {
            i.init();
        });

        this.createPending(order).subscribe(async (t: Tab) => {
            const tab = new Tab(t);
            this.tabService.setCurrent(tab);
            this.tabService.setEvent(tab);
            this.setCurrent(tab.orders.find(o => o.id === order.id || (!order.id && o.deviceId === this.deviceId)));
            this.loadingService.dismiss();
            await this.navController.navigateForward(`/confirm-order/${order.pickupLocation.id}`);
        }, async error => {
            if (error.status === 424 && !!error.error && !!error.error.message) {
                const errorDetails = JSON.parse(error.error.message);
                if (!!errorDetails.unavailableItemIds) {
                    const removing = order.items.filter(i => errorDetails.unavailableItemIds.includes(i.menuItem.id));
                    order.items = order.items.filter(i => !errorDetails.unavailableItemIds.includes(i.menuItem.id));
                    let errMessage = 'Some items are no longer available and are removed from your order:<br><ul>';
                    removing.forEach(i => errMessage += `<li>${i.menuItem.name}</li>`);
                    errMessage += '</ul>';
                    await this.toastService.message(errMessage);

                    if (order.items.length > 0) {
                        this.setCurrent(order);
                    } else {
                        this.setCurrent(null);
                        this.tabService.setCurrent(null);
                    }

                    this.pickupLocationMenuService.findByPickupLocationId(order.pickupLocation.id).subscribe(m => {
                        this.pickupLocationMenuService.setCurrent(new PickupLocationMenu(m));
                    });
                }
            } else if (error.status === 417) {
                await this.toastService.error('This location is not currently taking orders.');
            } else {
                // clean out local storage
                this.tabService.setCurrent(null);
                this.setCurrent(null);
                await this.toastService.error('A server error occurred.');
            }
            this.loadingService.dismiss();
        });
    }

    handlePendingError(error) {
        if (error.status === 424 && !!error.error && !!error.error.message) {
            try {
                const errorDetails = JSON.parse(error.error.message);
                const pickupLocationMenu = this.pickupLocationMenuService.currentValue;
                if (!!errorDetails.unavailableItemIds && !!pickupLocationMenu) {
                    this.pickupLocationMenuService.findByPickupLocationId(pickupLocationMenu.parentId).subscribe(m => {
                        this.pickupLocationMenuService.setCurrent(new PickupLocationMenu(m));
                    });
                }
            } catch (err) {
                console.log('Could not parse errors');
            }
        }
    }

    setCurrentPending(tab: Tab, order: Order) {
        if (!!tab && !!tab.orders) {
            if (order.id) {
                this.setCurrent(new Order(tab.orders.find(o => o.id === order.id)));
            } else {
                this.setCurrent(new Order(tab.orders.find(o => o.deviceId === this.deviceId && o.status === OrderStatuses.PENDING)));
            }
        }
    }

    setEvent(obj: Order) {
        super.setEvent(obj);
        if (!!obj && obj.parentId) {
            this.tabService.getSilent(obj.parentId).subscribe(t => {
                if (!!t) {
                    this.tabService.setEvent(new Tab(t));
                }
            });
        }
    }
}
